import axios from 'axios';

const DEVOPS_BASE_URL = (process.env.REACT_APP_DEVOPS_API_BASE_URL || process.env.DEVOPS_API_BASE_URL || '').replace(/\/$/, '');
const ORG_SLUG = (process.env.REACT_APP_DEVOPS_ORGANIZATION_NAME || '').trim();

const devopsApi = axios.create({
  baseURL: DEVOPS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

devopsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('cognito_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }
    return Promise.reject(new Error('Missing authentication token'));
  },
  (error) => Promise.reject(error)
);

devopsApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

export async function getConnections() {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/get-connections`;
  const resp = await devopsApi.get(url, { params: { userId, orgSlug: ORG_SLUG } });
  return Array.isArray(resp?.data?.data) ? resp.data.data : [];
}

export async function configureConnection(payload) {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const fd = new FormData();
  if (!ORG_SLUG) throw new Error('Missing organization configuration');
  fd.append('orgSlug', ORG_SLUG);
  fd.append('userId', userId);
  if (payload?.connectionName) fd.append('connectionName', payload.connectionName);
  if (payload?.infraType) fd.append('infraType', payload.infraType);
  if (payload?.connectionType) fd.append('connectionType', payload.connectionType);
  if (payload?.hostName) fd.append('hostName', payload.hostName);
  if (payload?.username) fd.append('username', payload.username);
  if (payload?.password) fd.append('password', payload.password);
  if (payload?.port) fd.append('port', payload.port);
  if (payload?.pemFile) fd.append('pemfile', payload.pemFile);

  const url = `/api/connection/configure`;
  const resp = await devopsApi.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data;
}

export async function getConnectionJobs(connectionId) {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/all-jobs`;
  const resp = await devopsApi.get(url, { params: { userId, connectionId, orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return Array.isArray(data?.data) ? data.data : [];
}

export async function addMonitoring({ monitoringName, monitoringUrl }) {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }
  if (!monitoringName || !monitoringUrl) {
    throw new Error('Both monitoringName and monitoringUrl are required');
  }

  const url = `/api/connection/monitoring`;
  const body = { orgSlug: ORG_SLUG, userId, monitoringName, monitoringUrl };
  const resp = await devopsApi.post(url, body, { headers: { 'Content-Type': 'application/json' } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data?.data ?? data;
}

export async function getServices() {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/services`;
  const resp = await devopsApi.get(url, { params: { userId, orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return Array.isArray(data?.data) ? data.data : [];
}

export async function deployService({ connectionId, service, projectId, serviceName }) {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/deploy-service`;
  const body = { orgSlug: ORG_SLUG, userId, connectionId, service, projectId, serviceName };
  const resp = await devopsApi.post(url, body, { headers: { 'Content-Type': 'application/json' } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data;
}

export async function executeCommand({ connectionId, command }) {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/execute-command`;
  const body = { orgSlug: ORG_SLUG, userId, connectionId, command };
  const resp = await devopsApi.post(url, body, { headers: { 'Content-Type': 'application/json' } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data?.data ?? data;
}

export async function getAdhocJobOutput(jobId, format = 'json') {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/adhoc-job-output`;
  const resp = await devopsApi.get(url, { params: { userId, jobId, format, orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data?.data ?? data;
}

export async function getJobOutput(jobId, format = 'json') {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/job-output`;
  const resp = await devopsApi.get(url, { params: { userId, jobId, format, orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data?.data ?? data;
}

export async function getMonitorings() {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/monitorings`;
  const resp = await devopsApi.get(url, { params: { userId, orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return Array.isArray(data?.data) ? data.data : [];
}

export async function getAwxConnection() {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  try {
    const parsed = JSON.parse(rawUser);
    if (!parsed?.sub) {
      throw new Error('User ID not found in user_info');
    }
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!ORG_SLUG) throw new Error('Missing organization configuration');

  const url = `/api/connection/get-awx-connection`;
  const resp = await devopsApi.get(url, { params: { orgSlug: ORG_SLUG } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  // returns { data: {...} } or { data: null }
  return data?.data ?? null;
}

export async function awxAccountSetup() {
  const rawUser = localStorage.getItem('user_info');
  if (!rawUser) {
    throw new Error('Missing user_info in localStorage');
  }
  let userId;
  let email;
  try {
    const parsed = JSON.parse(rawUser);
    userId = parsed?.sub;
    email = parsed?.email;
  } catch (e) {
    throw new Error('Invalid user_info format');
  }
  if (!userId) {
    throw new Error('User ID not found in user_info');
  }

  const url = `/api/connection/awx-account-setup`;
  const body = { orgSlug: ORG_SLUG, userId, email };
  const resp = await devopsApi.post(url, body, { headers: { 'Content-Type': 'application/json' } });
  const data = resp?.data;
  if (data?.error) {
    const msg = data?.error?.message || 'Request failed';
    const detail = data?.error?.detail ? `: ${data.error.detail}` : '';
    const err = new Error(`${msg}${detail}`);
    err.code = data?.error?.code;
    throw err;
  }
  return data?.data ?? data;
}

export default {
  getConnections,
  configureConnection,
  getConnectionJobs,
  getServices,
  deployService,
  executeCommand,
  getAdhocJobOutput,
  getJobOutput,
  getMonitorings,
  addMonitoring,
  getAwxConnection,
  awxAccountSetup,
};
